import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyEmail, resendOTP } from '../../services/emailVerificationService';
import './emailVerification.css';

const EmailVerification = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [isPeriodicVerification, setIsPeriodicVerification] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  useEffect(() => {
    // Get email from location state or localStorage
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem('verification_email');
    const isPeriodic = location.state?.isPeriodicVerification || false;

    if (emailFromState) {
      setEmail(emailFromState);
      setIsPeriodicVerification(isPeriodic);
      localStorage.setItem('verification_email', emailFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    } else {
      // No email found, redirect to signup
      navigate('/validate');
    }

    // Auto-focus first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [location, navigate]);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only take the last digit
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Clear errors when user types
    setError('');
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);

    // Focus on the next empty input or last input
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex].focus();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await verifyEmail(email, otpCode);
      setSuccess('Email verified successfully! Redirecting to login...');

      // Clear verification email from localStorage
      localStorage.removeItem('verification_email');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/signin', {
          state: {
            message: 'Email verified successfully! You can now log in.'
          }
        });
      }, 2000);

    } catch (error) {
      console.error('Verification error:', error);

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        if (status === 400) {
          // Show the backend's actual message ("Invalid OTP code..." vs
          // "OTP has expired...") instead of a blanket "expired" -- these
          // are two different problems (a wrong/stale code vs a genuinely
          // timed-out one) and collapsing them together made a same-second
          // code mismatch look like an expiry, which it usually isn't.
          setError(errorData.error || 'Invalid or expired verification code. Please check and try again.');
        } else if (status === 404) {
          setError('User not found. Please register again.');
        } else if (status === 429) {
          setError('Too many attempts. Please try again later.');
        } else {
          setError(errorData.detail || errorData.error || 'Verification failed. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      await resendOTP(email);
      setSuccess('New verification code sent to your email.');

      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }

      // Start 60-second cooldown
      setResendCooldown(60);

    } catch (error) {
      console.error('Resend OTP error:', error);

      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        if (status === 429) {
          setError('Too many requests. Please wait before requesting a new code.');
        } else {
          setError(errorData.detail || errorData.error || 'Failed to resend code. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  return (
    <div className="email-verification-container">
      <div className="verification-card">
        <h2 className="verification-title">
          {isPeriodicVerification ? 'Verify Your Identity' : 'Verify Your Email'}
        </h2>

        <p className="verification-subtitle">
          {isPeriodicVerification
            ? "For your security, we've sent a verification code to"
            : "We've sent a 6-digit verification code to"}
        </p>
        <p className="verification-email">{email}</p>

        <form onSubmit={handleSubmit} className="verification-form">
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="otp-input"
                disabled={loading}
              />
            ))}
          </div>

          {error && <div className="verification-error">{error}</div>}
          {success && <div className="verification-success">{success}</div>}

          <button
            type="submit"
            className="verify-btn"
            disabled={loading || otp.join('').length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="resend-section">
          <p className="resend-text">Didn't receive the code?</p>
          <button
            type="button"
            onClick={handleResendOTP}
            className="resend-btn"
            disabled={resendCooldown > 0 || resendLoading}
          >
            {resendLoading
              ? 'Sending...'
              : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend Code'
            }
          </button>
        </div>

        <div className="back-to-login">
          <button
            onClick={() => navigate('/signin')}
            className="back-link"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
